'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { BeneficiosSectionData } from '@/landings/interfaces/types';

interface BeneficioWithId {
	id: string;
	titulo: string;
	icono: string;
	descripcion: string;
}

// Datos de fallback
const BENEFICIOS_DEFAULT: BeneficiosSectionData = {
	titulo: 'Beneficios Clave',
	subtitulo: 'Descubre por qué somos tu mejor elección para el cuidado de tu vehículo',
	Beneficio: [
		{
			titulo: 'Seguridad',
			icono: '🛡️',
			descripcion: 'Una transmisión en mal estado puede fallar al acelerar o al subir pendientes. Mantenerla en buen estado reduce riesgos.'
		},
		{
			titulo: 'Confianza',
			icono: '💪',
			descripcion: 'La mantención regular (aceite, filtros, ajustes) asegura un funcionamiento óptimo y alarga la vida del sistema.'
		},
		{
			titulo: 'Ahorro',
			icono: '💰',
			descripcion: 'Detectar y resolver problemas a tiempo reduce el riesgo de fallas graves que pueden implicar el cambio completo de la caja.'
		},
		{
			titulo: 'Precaución',
			icono: '⚠️',
			descripcion: 'El aceite viejo acumula residuos metálicos y desgaste que pueden dañar componentes internos. El cambio regular protege el sistema.'
		}
	]
};

export function BeneficiosSection({ data = BENEFICIOS_DEFAULT }: { data?: BeneficiosSectionData }) {
	const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
	const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());
	const [timelineProgress, setTimelineProgress] = useState(0);
	const sectionRef = useRef<HTMLElement>(null);
	const timelineRef = useRef<HTMLDivElement>(null);

	// Tomar solo los primeros 4 beneficios y agregarles un ID único
	const beneficios: BeneficioWithId[] = useMemo(() => {
		const beneficiosToUse = data?.Beneficio || BENEFICIOS_DEFAULT.Beneficio;
		return beneficiosToUse.slice(0, 4).map((beneficio, index) => ({
			...beneficio,
			id: `beneficio-${index}`
		}));
	}, [data?.Beneficio]);

	// Efecto para observar el scroll y animar la línea de tiempo
	useEffect(() => {
		const handleScroll = () => {
			if (!sectionRef.current || !timelineRef.current) return;

			const sectionRect = sectionRef.current.getBoundingClientRect();
			const windowHeight = window.innerHeight;

			// Calcular el progreso de la línea de tiempo
			if (sectionRect.top < windowHeight && sectionRect.bottom > 0) {
				const scrolled = windowHeight - sectionRect.top;
				const total = sectionRect.height + windowHeight;
				const progress = Math.min(Math.max(scrolled / total, 0), 1);
				setTimelineProgress(progress * 100);
			}

			// Verificar qué items son visibles y expandirlos automáticamente
			beneficios.forEach((beneficio, index) => {
				const itemElement = document.getElementById(`beneficio-${beneficio.id}`);
				if (itemElement) {
					const itemRect = itemElement.getBoundingClientRect();
					const isVisible = itemRect.top < windowHeight * 0.7 && itemRect.bottom > 0;
					
					if (isVisible && !visibleItems.has(beneficio.id)) {
						setVisibleItems(prev => new Set(prev).add(beneficio.id));
						// Expandir automáticamente cuando se hace visible por primera vez
						setTimeout(() => {
							setExpandedItems(prev => new Set(prev).add(beneficio.id));
						}, 600); // Delay para que primero aparezca el item
					}
				}
			});
		};

		// Ejecutar al cargar y en cada scroll
		handleScroll();
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, [beneficios, visibleItems]);

	const toggleExpanded = (id: string) => {
		setExpandedItems(prev => {
			const newSet = new Set(prev);
			if (newSet.has(id)) {
				newSet.delete(id);
			} else {
				newSet.add(id);
			}
			return newSet;
		});
	};

	return (
		<section ref={sectionRef} className="landing-beneficios">
			<div className="landing-beneficios__background">
				<div className="background-gradient background-gradient--1"></div>
				<div className="background-gradient background-gradient--2"></div>
			</div>
			
			<div className="landing-beneficios-container">
				<header className="landing-beneficios__header">
					<h2>{data.titulo || 'Beneficios Clave'}</h2>
					{data.subtitulo && <p>{data.subtitulo}</p>}
				</header>

				<div className="landing-beneficios__timeline" ref={timelineRef}>
					<div className="timeline-line">
						<div 
							className="timeline-progress"
							style={{ height: `${timelineProgress}%` }}
						/>
					</div>

					<div className="timeline-items">
						{beneficios.map((beneficio, index) => {
							const isEven = index % 2 === 0;
							const isVisible = visibleItems.has(beneficio.id);
							const isExpanded = expandedItems.has(beneficio.id);

							return (
								<div
									key={beneficio.id}
									id={`beneficio-${beneficio.id}`}
									className={`timeline-item ${isEven ? 'timeline-item--left' : 'timeline-item--right'} ${isVisible ? 'timeline-item--visible' : ''}`}
								>
									<div className="timeline-point">
										<div className="timeline-point-inner" />
									</div>

									<div 
										className={`timeline-card ${isExpanded ? 'timeline-card--expanded' : ''}`}
										onClick={() => toggleExpanded(beneficio.id)}
									>
										<div className="timeline-card__header">
											<span className="timeline-card__icon">{beneficio.icono}</span>
											<h3 className="timeline-card__title">{beneficio.titulo}</h3>
											<button className="timeline-card__toggle">
												<svg 
													width="20" 
													height="20" 
													viewBox="0 0 20 20" 
													fill="currentColor"
													className={`toggle-icon ${isExpanded ? 'toggle-icon--expanded' : ''}`}
												>
													<path d="M5 8l5 5 5-5z"/>
												</svg>
											</button>
										</div>
										
										<div className={`timeline-card__content ${isExpanded ? 'timeline-card__content--expanded' : ''}`}>
											<p className="timeline-card__description">{beneficio.descripcion}</p>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</section>
	);
}